import { Grid, Stack, Typography } from '@mui/material';

import { ChangeEvent, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as InfoIcon } from 'common/assets/images/info-icon.svg';
import { ReactComponent as InfoSelectedIcon } from 'common/assets/images/info-variant.svg';
import { ReactComponent as LabsIcon } from 'common/assets/images/labs.svg';
import { ReactComponent as LabsFillIcon } from 'common/assets/images/labsfill.svg';
import { ReactComponent as PromptIcon } from 'common/assets/images/orgchart.svg';
import { ReactComponent as PromptSelectedIcon } from 'common/assets/images/orgchartfill.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import {
	StyledTab,
	StyledTabs,
	TabPanel,
} from 'common/components/TabBar/TabBar';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { TOAST_MESSAGE_STATUS } from 'common/constants/ToastMessageConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setDetailPaneTabIndex,
	setPrompt,
	setMessages,
	setSystemText,
	setIsReloadTableData,
	setAIModelPromptOptions,
} from 'common/store/reducer/promptBuilderReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { addPrompt, fetchAIModelsOptions, updatePrompt } from '../api';
import { IPromptProps } from '../api/type-def';
import {
	disableSaveHandler,
	getModifiedStoreData,
} from '../PromptBuilderUtils';

import ConfigurePane from './ConfigurePane/ConfigurePane';
import {
	BUTTON_ACTIONS,
	PANE_TAB_VALUES,
	PROMPT_SUB_TITLE_LABEL,
	PROMPT_TITLE_LABEL,
	TOAST_MESSAGE,
	TYPE_DISPLAY_MAPPING,
	TYPE_OPTIONS,
	initialPrompt,
} from './PromptBuilderConstants';
import PromptDetails from './PromptDetails/PromptDetails';
import PromptSettings from './PromptSettings/PromptSettings';

import styles from './PromptBuilderDetails.module.scss';

interface IDetailLayout {
	isModalOpen: boolean;
	onCloseModal: () => void;
	resetHandler: () => void;
}

const PromptBuilderDetailLayout = (props: IDetailLayout) => {
	const dispatch = useDispatch();
	const { PUBLISH, SAVE_DRAFT, GO_BACK } = BUTTON_ACTIONS;
	const { CHAT_COMPLETION } = TYPE_OPTIONS;
	const {
		PUBLISH_SUCCESS,
		PUBLISH_FAILURE,
		SAVE_DRAFT_SUCCESS,
		SAVE_DRAFT_FAILURE,
		DUPLICATE_PROMPTS_FAILURE,
	} = TOAST_MESSAGE;
	const { TOAST_SUCCESS, TOAST_ERROR, TOAST_CONFLICT_ERROR } =
		TOAST_MESSAGE_STATUS;
	const { OK, CONFLICT } = HTTP_STATUS_CODES;
	const { CHAT_COMPLETION_DISPLAY } = TYPE_DISPLAY_MAPPING;

	const [isLoading, setIsLoading] = useState<string>('');

	const { isModalOpen, onCloseModal, resetHandler } = props;

	const { TITLE, PROMPT, CONFIGURE, TEST } = PANE_TAB_VALUES;

	const isPromptEdit: boolean = useSelector(
		(state: RootState) => state.promptBuilder.isPromptEdit,
	);

	const aiModelOptions: any = useSelector(
		(state: RootState) => state.promptBuilder.aiModelOptions,
	);
	const prompt: IPromptProps = useSelector(
		(state: RootState) => state.promptBuilder.prompt,
	);

	const systemText: string = useSelector(
		(state: RootState) => state.promptBuilder.systemText,
	);

	const detailPaneTabIndex = useSelector(
		(state: RootState) => state.promptBuilder.detailPaneIndex,
	);

	const [disableTestPane, setDisableTestPane] = useState<boolean>(true);

	const detailTabs = [
		{ name: TITLE, icon: <InfoIcon />, selectedIcon: <InfoSelectedIcon /> },
		{
			name: PROMPT,
			icon: <PromptIcon />,
			selectedIcon: <PromptSelectedIcon />,
		},
		{
			name: CONFIGURE,
			icon: <LabsIcon />,
			selectedIcon: <LabsFillIcon />,
		},
		{
			name: TEST,
			disabled: disableTestPane,
			icon: <LabsIcon />,
			selectedIcon: <LabsFillIcon />,
		},
	];

	const testPaneHandler = (status: boolean) => {
		if (prompt?.is_draft && prompt?.prompt) {
			// Enables Test tab for Draft prompts
			setDisableTestPane(false);
		} else setDisableTestPane(status);
	};

	const existingPromptDetailsFromStore: any = useSelector(
		(state: RootState) => state.promptBuilder.existingPromptDetails,
	);

	const handleDetailPaneTabChange = (
		event: React.SyntheticEvent,
		index: number,
	) => {
		dispatch(setDetailPaneTabIndex(index));
	};

	const getExistingData = () => {
		const { existingDetails } = getModifiedStoreData(
			existingPromptDetailsFromStore,
		);
		return existingDetails;
	};

	const { systemMsg, storedMsgs } = getModifiedStoreData(
		existingPromptDetailsFromStore,
	);
	const existingPromptData = getExistingData();

	const fetchStatusOfDetails = (status: string, saveType: string) => {
		if (status?.toLowerCase() === TOAST_SUCCESS?.toLowerCase()) {
			dispatch(
				setToastMessage({
					message: saveType === PUBLISH ? PUBLISH_SUCCESS : SAVE_DRAFT_SUCCESS,
					severity: TOAST_SUCCESS,
					isToastOpen: true,
				}),
			);
			resetHandler();
		} else if (status?.toLowerCase() === TOAST_CONFLICT_ERROR?.toLowerCase()) {
			dispatch(
				setToastMessage({
					message: DUPLICATE_PROMPTS_FAILURE,
					severity: TOAST_ERROR,
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: saveType === PUBLISH ? PUBLISH_FAILURE : SAVE_DRAFT_FAILURE,
					severity: TOAST_ERROR,
					isToastOpen: true,
				}),
			);
		}
	};

	/**
	 * @description method to save the prompt details on click of save btn
	 */
	const onDetailsSaveHandler = async (saveType: string) => {
		const { key, ...newPrompt } = prompt;
		setIsLoading(saveType);
		const updatedPrompt = {
			...newPrompt,
			type:
				newPrompt.type === CHAT_COMPLETION
					? CHAT_COMPLETION_DISPLAY
					: newPrompt.type,
			ai_model_key: newPrompt.aiModelKey,
			is_draft: saveType === SAVE_DRAFT,
		};
		let res;
		if (isPromptEdit) {
			res = await updatePrompt(key, updatedPrompt);
		} else {
			res = await addPrompt(key, updatedPrompt);
		}
		setIsLoading('');
		if (res?.statusCode === OK) {
			fetchStatusOfDetails(TOAST_SUCCESS, saveType);
			dispatch(setIsReloadTableData(true));
			dispatch(setDetailPaneTabIndex(0));
			onCloseModal();
		} else if (res?.statusCode === CONFLICT) {
			fetchStatusOfDetails(TOAST_CONFLICT_ERROR, saveType);
		} else {
			fetchStatusOfDetails(TOAST_ERROR, saveType);
		}
	};
	const isPublishDisabled: boolean =
		!prompt?.is_draft ||
		!isPromptEdit ||
		disableSaveHandler(prompt, isPromptEdit, systemText, existingPromptData);

	const isDraftDisabled: boolean =
		!prompt?.is_draft ||
		disableSaveHandler(prompt, isPromptEdit, systemText, existingPromptData);

	useEffect(() => {
		dispatch(setPrompt(isPromptEdit ? existingPromptData : initialPrompt));
		dispatch(setMessages(isPromptEdit ? storedMsgs : []));
		dispatch(setSystemText(isPromptEdit ? systemMsg : ''));

		return () => {
			dispatch(setDetailPaneTabIndex(0));
		};
	}, []);

	/**
	 * @description dynamic method to set the fields text on change
	 * @param key, event
	 */
	const onFieldsChangeHandler = (
		keyValue: string,
		event: ChangeEvent<HTMLInputElement>,
	) => {
		dispatch(setPrompt({ ...prompt, [keyValue]: event?.target?.value }));
	};

	/**
	 * @description dynamic method to set the fields text on change
	 * @param key, event
	 */
	const onFieldValueChangeHandler = (key: string, value: any) => {
		dispatch(setPrompt({ ...prompt, [key]: value }));
	};

	const getAiModelOptions = async () => {
		const response = await fetchAIModelsOptions();
		if (response?.statusCode === OK && response.responseBody) {
			const options = response.responseBody;
			if (options.length) {
				dispatch(setAIModelPromptOptions(options));
			}
		}
	};

	const checkTabDisable = (tabName: string) => {
		switch (tabName) {
			case PROMPT:
				return !(prompt.key && prompt.description);
			case CONFIGURE:
				return !(
					prompt.key &&
					prompt.description &&
					prompt.type &&
					prompt.aiModelKey
				);
			case TEST:
				return !(
					prompt.key &&
					prompt.description &&
					prompt.type &&
					prompt.aiModelKey &&
					prompt.prompt
				);
			default:
				return false;
		}
	};

	useEffect(() => {
		if (aiModelOptions.length === 0) {
			getAiModelOptions();
		}
	}, []);
	return (
		<ModalEle
			dataTestId="prompt-builder-detail-layout"
			open={isModalOpen}
			handleClose={onCloseModal}
			sxBoxProps={{ width: '90%', height: '30%', margin: 'auto' }}
		>
			<Grid container>
				<Grid md={12}>
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['header-text']} data-testid="header">
							{PROMPT_TITLE_LABEL}
						</Typography>
						<CloseIcon
							onClick={onCloseModal}
							className={styles['add-closemodal']}
							data-testid="close-icon"
						/>
					</Stack>
				</Grid>
				<Grid md={12}>
					<Typography variant="body2" className={styles['paragraph-text']}>
						{PROMPT_SUB_TITLE_LABEL}
					</Typography>
				</Grid>
				<Grid md={3}>
					<StyledTabs
						orientation="vertical"
						variant="scrollable"
						value={detailPaneTabIndex || 0}
						className={styles['tab-container']}
						onChange={handleDetailPaneTabChange}
						aria-label="scrollable prevent tabs"
					>
						{detailTabs.map((res, index) => (
							<StyledTab
								key={res.name}
								data-testid={`tab-${res.name.toLowerCase().replace(' ', '')}`}
								icon={
									detailPaneTabIndex === index ? res.selectedIcon : res.icon
								}
								disabled={checkTabDisable(res.name)}
								iconPosition="start"
								label={res.name}
								value={index}
								className={
									detailPaneTabIndex === index
										? styles['tab-selected']
										: styles['tab-select']
								}
								greyBg
							/>
						))}
					</StyledTabs>
				</Grid>
				<Grid md={9} className={styles['prompt-container']}>
					<TabPanel value={detailPaneTabIndex} index={0} key="tab-panel-name">
						<PromptDetails
							isEdit={isPromptEdit}
							onPromptChangeHandler={onFieldsChangeHandler}
							onFieldValueChangeHandler={onFieldValueChangeHandler}
						/>
					</TabPanel>
					<TabPanel value={detailPaneTabIndex} index={1} key="tab-panel-prompt">
						<PromptSettings />
					</TabPanel>
					<TabPanel value={detailPaneTabIndex} index={2} key="tab-panel-config">
						<ConfigurePane
							onPromptChangeHandler={onFieldsChangeHandler}
							isTestPane={false}
							isEdit={isPromptEdit}
							resetHandler={resetHandler}
							onCloseModal={onCloseModal}
							testPaneHandler={testPaneHandler}
						/>
					</TabPanel>
					<TabPanel value={detailPaneTabIndex} index={3} key="tab-panel-test">
						<ConfigurePane
							onPromptChangeHandler={onFieldsChangeHandler}
							isTestPane
							isEdit={isPromptEdit}
							resetHandler={resetHandler}
							onCloseModal={onCloseModal}
							testPaneHandler={testPaneHandler}
						/>
					</TabPanel>
				</Grid>
				<Grid md={12}>
					<Stack className={styles['button-container']}>
						<ButtonComp
							btnType="secondary"
							clickHandler={onCloseModal}
							data-testid="goback-btn"
						>
							{toSentenceCase(GO_BACK)}
						</ButtonComp>
						<ButtonComp
							clickHandler={() => onDetailsSaveHandler(SAVE_DRAFT)}
							btnType="secondary"
							data-testid="save-draft-btn"
							disabled={isDraftDisabled}
							loading={isLoading === SAVE_DRAFT}
						>
							{toSentenceCase(SAVE_DRAFT)}
						</ButtonComp>
						<ButtonComp
							clickHandler={() => onDetailsSaveHandler(PUBLISH)}
							btnType="primary"
							data-testid="publish-btn"
							disabled={isPublishDisabled}
							loading={isLoading === PUBLISH}
						>
							{toSentenceCase(PUBLISH)}
						</ButtonComp>
					</Stack>
				</Grid>
			</Grid>
		</ModalEle>
	);
};

export default PromptBuilderDetailLayout;
